/* Product page behaviour for the two rebuilt blocks.

   `snippets/sn-reviews.liquid` renders the filter row with the `hidden` attribute, because without a
   script those controls would do nothing. This file reveals them and wires them to the list that is
   already in the page, so filtering and sorting cost no request. It also makes the rating snapshot
   rows filter on click — the reference says "Select a row below to filter reviews" — and carries the
   star a visitor clicks in "Review this Product" into the form's rating field.

   The second job is the jump-link bar. Some captured product pages arrive with that bar empty, so
   the links are rebuilt from the anchors the page actually has, in the reference's order.

   Everything degrades cleanly: with this file absent the reviews stay listed newest first, the
   controls stay hidden, and the bar keeps whatever links the capture gave it. */
(() => {
  const parseDate = review => {
    const value = Date.parse(review.dataset.date || '');
    return Number.isNaN(value) ? 0 : value;
  };

  // The reference prints "3 months ago" rather than a calendar date; the markup keeps the real date
  // in `datetime` so the absolute value is still available to a reader or a crawler.
  const RELATIVE_STEPS = [
    [31536000, 'year'],
    [2592000, 'month'],
    [604800, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
  ];

  const relativeLabel = value => {
    const seconds = (Date.now() - value) / 1000;
    if (seconds < 3600) return 'just now';
    for (const [size, unit] of RELATIVE_STEPS) {
      const amount = Math.floor(seconds / size);
      if (amount >= 1) return `${amount} ${unit}${amount === 1 ? '' : 's'} ago`;
    }
    return 'just now';
  };

  const setupDates = root => root.querySelectorAll('.sn-review-date time').forEach(time => {
    // a review read from the reference already prints the reference's own wording
    if (time.hasAttribute('data-sn-date-fixed')) return;
    const value = Date.parse(time.getAttribute('datetime') || '');
    if (!Number.isNaN(value) && value <= Date.now()) time.textContent = relativeLabel(value);
  });

  const setupReviews = root => {
    setupDates(root);
    const list = root.querySelector('[data-sn-review-list]');
    const filters = root.querySelector('[data-sn-review-filters]');
    const status = root.querySelector('[data-sn-review-status]');
    if (!list || !filters) return;

    const reviews = [...list.querySelectorAll('[data-sn-review]')];
    if (!reviews.length) return;
    filters.hidden = false;

    const search = filters.querySelector('[data-sn-review-search]');
    const rating = filters.querySelector('[data-sn-review-rating]');
    const sort = filters.querySelector('[data-sn-review-sort]');
    const snapshotRows = [...root.querySelectorAll('[data-sn-snapshot-row]')];
    const total = reviews.length;

    const apply = () => {
      const term = (search?.value || '').trim().toLowerCase();
      const wanted = rating?.value || '';
      let shown = 0;
      reviews.forEach(review => {
        const matchesRating = !wanted || review.dataset.rating === wanted;
        const matchesTerm = !term || review.textContent.toLowerCase().includes(term);
        const visible = matchesRating && matchesTerm;
        review.hidden = !visible;
        if (visible) shown += 1;
      });

      const order = sort?.value || 'newest';
      const sorted = [...reviews].sort((a, b) => {
        if (order === 'highest') return b.dataset.rating - a.dataset.rating;
        if (order === 'lowest') return a.dataset.rating - b.dataset.rating;
        if (order === 'helpful') return (b.dataset.helpful || 0) - (a.dataset.helpful || 0);
        return parseDate(b) - parseDate(a);
      });
      sorted.forEach(review => list.append(review));

      snapshotRows.forEach(row => row.setAttribute('aria-pressed', String(row.dataset.rating === wanted)));
      if (status) {
        status.textContent = shown === total
          ? `${total} ${total === 1 ? 'Review' : 'Reviews'}`
          : `${shown} of ${total} ${total === 1 ? 'Review' : 'Reviews'}`;
      }
    };

    [search, rating, sort].forEach(control => {
      control?.addEventListener('input', apply);
      control?.addEventListener('change', apply);
    });

    // clicking a snapshot row filters to that rating; clicking the active one clears the filter
    snapshotRows.forEach(row => row.addEventListener('click', () => {
      if (!rating) return;
      rating.value = rating.value === row.dataset.rating ? '' : row.dataset.rating;
      apply();
      list.scrollIntoView({ block: 'start' });
    }));

    // the invite stars pre-select the rating in the form the anchor jumps to
    root.querySelectorAll('[data-sn-invite-rating]').forEach(star => star.addEventListener('click', () => {
      const field = root.querySelector('[data-sn-review-rating-field]');
      if (field) field.value = star.dataset.snInviteRating;
    }));

    apply();
  };

  // Reference order; anything else the capture provided keeps its own position after these.
  const JUMP_LINKS = [
    ['#highlights', 'Highlights'],
    ['#in-the-box', 'In the box'],
    ['#compare', 'Compare'],
  ];

  const setupJumpLinks = () => {
    const bar = document.querySelector('.jump-links.tabs');
    if (!bar) return;
    // Some captured product pages have no `.jumplink-review#reviews` wrapper, so the injected
    // Reviews link would point at nothing; give the rebuilt block that id when it is free.
    if (!document.getElementById('reviews')) {
      const block = document.querySelector('[data-sn-reviews]');
      if (block) block.id = 'reviews';
    }
    JUMP_LINKS.forEach(([href, label], index) => {
      if (bar.querySelector(`a[href="${href}"]`)) return;
      if (!document.querySelector(href)) return;
      const link = document.createElement('a');
      link.className = 'link-small tab';
      link.href = href;
      link.textContent = label;
      link.style.order = String(index - JUMP_LINKS.length); // stay ahead of the injected Reviews link
      bar.prepend(link);
    });
    if (!bar.querySelector('.tab.active')) bar.querySelector('a')?.classList.add('active');
  };

  // The reference puts a pair of arrows over the customer photo strip. The strip already scrolls with
  // the finger; these give the desktop the same control, and each hides itself at its end of the run.
  const setupGallery = root => {
    const track = root.querySelector('.sn-review-gallery-track');
    if (!track || track.parentElement.classList.contains('sn-review-gallery-frame')) return;

    const frame = document.createElement('div');
    frame.className = 'sn-review-gallery-frame';
    track.parentElement.insertBefore(frame, track);
    frame.append(track);

    const make = (direction, label, glyph) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sn-review-gallery-arrow sn-review-gallery-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.textContent = glyph;
      button.addEventListener('click', () => {
        const step = track.clientWidth * 0.8;
        track.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' });
      });
      frame.append(button);
      return button;
    };

    const prev = make('prev', 'Previous photos', '‹');
    const next = make('next', 'More photos', '›');

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft <= 1;
      next.hidden = max <= 1 || track.scrollLeft >= max - 1;
    };
    track.addEventListener('scroll', sync);
    addEventListener('resize', sync);
    sync();
  };

  const start = () => {
    document.querySelectorAll('[data-sn-reviews]').forEach(root => {
      setupReviews(root);
      setupGallery(root);
    });
    setupJumpLinks();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
